import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaPrint, FaDownload, FaTimes, FaSchool, FaUser, FaRupeeSign, FaCalendarAlt, FaPhone } from 'react-icons/fa';
import toast from 'react-hot-toast';

const PaymentReceipt = ({ payment, onClose }) => {
  const receiptRef = useRef();

  const handlePrint = () => {
    const printContent = receiptRef.current;
    const originalContents = document.body.innerHTML;
    
    // Create print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Payment Receipt - Vidya Bharati School</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              padding: 40px; 
              max-width: 800px; 
              margin: 0 auto;
              background: white;
            }
            .receipt {
              border: 1px solid #ddd;
              border-radius: 10px;
              padding: 30px;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #3B82F6;
              padding-bottom: 20px;
              margin-bottom: 20px;
            }
            .header h1 {
              color: #1e293b;
              font-size: 24px;
              margin: 0;
            }
            .header p {
              color: #64748b;
              margin: 5px 0;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              padding: 10px 0;
              border-bottom: 1px solid #f1f5f9;
            }
            .detail-row .label {
              color: #64748b;
              font-weight: 500;
            }
            .detail-row .value {
              color: #1e293b;
              font-weight: 600;
            }
            .total {
              display: flex;
              justify-content: space-between;
              padding: 15px 0;
              margin-top: 10px;
              border-top: 2px solid #3B82F6;
              font-size: 18px;
            }
            .total .label {
              font-weight: 700;
              color: #1e293b;
            }
            .total .value {
              font-weight: 700;
              color: #3B82F6;
            }
            .status {
              display: inline-block;
              padding: 5px 15px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              background: #dcfce7;
              color: #166534;
            }
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #e2e8f0;
              color: #94a3b8;
              font-size: 12px;
            }
            @media print {
              body { padding: 20px; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <div class="no-print" style="text-align:center;margin-top:30px;">
            <button onclick="window.print()" style="padding:10px 30px;background:#3B82F6;color:white;border:none;border-radius:5px;cursor:pointer;margin-right:10px;">🖨️ Print</button>
            <button onclick="window.close()" style="padding:10px 30px;background:#ef4444;color:white;border:none;border-radius:5px;cursor:pointer;">Close</button>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownload = () => {
    toast.success('Receipt downloaded successfully!');
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Receipt Content */}
        <div ref={receiptRef} className="p-8">
          <div className="receipt">
            {/* Header */}
            <div className="header">
              <div className="flex items-center justify-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <FaSchool className="text-white text-2xl" />
                </div>
                <h1>Vidya Bharati School</h1>
              </div>
              <p>📞 +91 97316 66652</p>
              <p>✉️ vbpsbhalki@gmail.com</p>
              <p>📍 Near Subhash Chowk, Bhalki - 585328</p>
            </div>

            {/* Receipt Title */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Payment Receipt</h2>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className="text-green-600">✅ Payment Successful</span>
                <span className="status">PAID</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Receipt #: {payment?.receiptId || 'RCP-' + Date.now().toString().slice(-8)}
              </p>
              <p className="text-sm text-gray-500">
                Date: {new Date().toLocaleDateString('en-IN', { 
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit'
                })}
              </p>
            </div>

            {/* Payment Details */}
            <div className="space-y-2">
              <div className="detail-row">
                <span className="label"><FaUser className="inline mr-2" /> Student Name</span>
                <span className="value">{payment?.studentName || 'Student User'}</span>
              </div>
              <div className="detail-row">
                <span className="label"><FaUser className="inline mr-2" /> Student ID</span>
                <span className="value">{payment?.studentId || 'STU1001'}</span>
              </div>
              <div className="detail-row">
                <span className="label"><FaCalendarAlt className="inline mr-2" /> Fee Type</span>
                <span className="value">{payment?.feeMonth || 'Monthly Fee'}</span>
              </div>
              <div className="detail-row">
                <span className="label"><FaPhone className="inline mr-2" /> Payment Method</span>
                <span className="value">PhonePe UPI</span>
              </div>
            </div>

            {/* Total */}
            <div className="total">
              <span className="label">Total Amount</span>
              <span className="value">
                <FaRupeeSign className="inline" /> {payment?.amount || 0}
              </span>
            </div>

            {/* Status */}
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200 flex items-center justify-center gap-2">
              <FaCheckCircle className="text-green-600" />
              <span className="text-green-700 font-semibold">Payment Successful</span>
            </div>

            {/* Footer */}
            <div className="footer">
              <p>This is a system generated receipt. Valid without signature.</p>
              <p className="mt-1">Thank you for your payment!</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-t border-gray-100 flex flex-wrap gap-3">
          <button
            onClick={handlePrint}
            className="flex-1 min-w-[120px] px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition flex items-center justify-center gap-2"
          >
            <FaPrint /> Print Receipt
          </button>
          <button
            onClick={handleDownload}
            className="flex-1 min-w-[120px] px-4 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition flex items-center justify-center gap-2"
          >
            <FaDownload /> Download PDF
          </button>
          <button
            onClick={onClose}
            className="flex-1 min-w-[120px] px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition flex items-center justify-center gap-2"
          >
            <FaTimes /> Close
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default PaymentReceipt;
